import React, { useState, useEffect } from "react";
import { Toaster, toast } from "react-hot-toast";
import {
  updateMediaSource,
  fetchUserData,
  fetchAllAssigedObjects,
  assignTargetToUsers,
} from "../Services/APIService";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import { ActionMeta, MultiValue } from "react-select";

interface viewDatas {
  sourceId: number;
  sourceName: string;
  isActive: boolean;
  createdBy: number;
  createdOn: string;
  isAssignTabShown?: boolean;
}

interface UpdateCardProps {
  viewData: viewDatas | null;
  onUpdateComplete: () => void;
  onCancel: () => void;
}

interface FormData {
  sourceId: number | "";
  sourceName: string;
  isActive: boolean;
}

interface SelectOption {
  value: number;
  label: string;
  userData: User;
}

interface User {
  userId: number;
  userName: string;
  email: string;
}

interface AssignmentData {
  tenantId: number;
  assignmentTypeId: number;
  assignmentObjectId: number;
  isAssigned: boolean;
  listOfUsers: { userId: number }[];
  createdBy: number;
}

interface ValidationErrors {
  assignmentType?: string;
}

const UpdateCard: React.FC<UpdateCardProps> = ({
  viewData,
  onUpdateComplete,
  onCancel,
}) => {
  const [formData, setFormData] = useState<FormData>({
    sourceId: "",
    sourceName: "",
    isActive: true,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // For tab
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("tab1");
  const [isSubmittingAssignment, setIsSubmittingAssignment] = useState(false);

  // For assignment
  const requiredField = <span className="text-danger ms-1">*</span>;
  const [userOptions, setUserOptions] = useState<SelectOption[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<SelectOption[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [initialAssignedUsers, setInitialAssignedUsers] = useState<number[]>(
    []
  );
  const [currentSourceId, setCurrentSourceId] = useState<number | null>(null);

  const [assignmentData, setAssignmentData] = useState<AssignmentData>({
    tenantId: 0,
    assignmentTypeId: 0,
    assignmentObjectId: 0,
    isAssigned: true,
    listOfUsers: [],
    createdBy: 0,
  });

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
  };

  // Set form data when viewData changes
  useEffect(() => {
    if (viewData) {
      setFormData({
        sourceId: viewData.sourceId,
        sourceName: viewData.sourceName,
        isActive: viewData.isActive,
      });
      setCurrentSourceId(viewData.sourceId);
      setActiveTab(viewData.isAssignTabShown ? "tab3" : "tab1");
      const loadAssignedUsers = async () => {
        try {
          const [usersData] = await Promise.all([fetchUserData()]);
          const assignedData = await fetchAllAssigedObjects(
            viewData.sourceId,
            3
          );
          if (assignedData && assignedData.length > 0) {
            const assignedUsers = assignedData.map((assignment) => ({
              value: assignment.userId,
              label: assignment.userName,
              userData: {
                userId: assignment.userId,
                userName: assignment.userName,
                email: "",
              },
            }));
            setSelectedUsers(assignedUsers);
          }
          const userIds = assignedData.map((user) => user.userId);
          setInitialAssignedUsers(userIds);
        } catch (error) {
          console.error("Error loading assigned users:", error);
        }
      };
      loadAssignedUsers();
    }
  }, [viewData]);

  // Fetch user data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [usersData] = await Promise.all([fetchUserData()]);
        setUsers(usersData);
        const options = usersData.map((user) => ({
          value: user.userId,
          label: `${user.userName} (${user.email})`,
          userData: user,
        }));
        setUserOptions(options);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load required data");
      } finally {
        setIsLoading(false);
        setIsLoadingUsers(false);
      }
    };
    fetchData();
  }, []);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (formData.sourceId === "" || isNaN(Number(formData.sourceId))) {
      setError("Please enter a valid Source ID");
      return;
    }

    try {
      setIsLoading(true);
      const userId = localStorage.getItem("UserId");
      const tenantId = localStorage.getItem("tenantId");

      if (!userId || !tenantId) {
        throw new Error("User ID or Tenant ID not found in localStorage");
      }

      const payload = {
        sourceId: Number(formData.sourceId),
        sourceName: formData.sourceName.trim().replace(/\s+/g, " "),
        isActive: formData.isActive,
        tenantId: parseInt(tenantId),
        modifieddBy: parseInt(userId),
      };

      const result = await updateMediaSource(payload);
      console.log("Response from updateMediaSource:", result);

      if (result.data.isUpdated) {
        toast.success("Media Source Updated Successfully!");
        onUpdateComplete();
      } else {
        toast.error(result.data.message || "Media Source update failed!");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
      setIsLoadingUsers(false);
    }
  };

  const validateAssignmentData = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (
      !assignmentData.listOfUsers ||
      assignmentData.listOfUsers.length === 0
    ) {
      newErrors.assignmentType = "Please select at least one user";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleAssignmentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateAssignmentData()) {
      toast.error("Please fix the errors in the assignment form");
      return;
    }

    try {
      setIsSubmittingAssignment(true);
      const tenantId = localStorage.getItem("tenantId");
      const userId = localStorage.getItem("UserId");
      const currentSelectedUserIds = selectedUsers.map((user) => user.value);

      const usersToUnassign = initialAssignedUsers.filter(
        (userId) => !currentSelectedUserIds.includes(userId)
      );
      const usersToAssign = currentSelectedUserIds.filter(
        (userId) => !initialAssignedUsers.includes(userId)
      );

      if (usersToUnassign.length > 0) {
        const unassignData: AssignmentData = {
          tenantId: parseInt(tenantId || "0"),
          assignmentTypeId: 3,
          assignmentObjectId: currentSourceId || 0,
          isAssigned: false,
          listOfUsers: usersToUnassign.map((userId) => ({ userId })),
          createdBy: userId ? parseInt(userId) : 0,
        };
        await assignTargetToUsers(unassignData);
      }

      if (usersToAssign.length > 0) {
        const assignData: AssignmentData = {
          tenantId: parseInt(tenantId || "0"),
          assignmentTypeId: 3,
          assignmentObjectId: currentSourceId || 0,
          isAssigned: true,
          listOfUsers: usersToAssign.map((userId) => ({ userId })),
          createdBy: userId ? parseInt(userId) : 0,
        };
        await assignTargetToUsers(assignData);
      }

      toast.success("Users assigned successfully");
      setSelectedUsers([]);
      setAssignmentData({
        ...assignmentData,
        assignmentTypeId: 0,
        listOfUsers: [],
      });
      onUpdateComplete();
    } catch (error) {
      console.error("Error assigning users:", error);
      toast.error("An error occurred while assigning users");
    } finally {
      setIsSubmittingAssignment(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    onCancel();
  };

  const handleMultiSelectChange = (
    newValue: MultiValue<SelectOption>,
    actionMeta: ActionMeta<SelectOption>
  ) => {
    if (!newValue || newValue.length === 0) {
      setSelectedUsers([]);
      setAssignmentData({
        ...assignmentData,
        listOfUsers: [],
      });
      return;
    }

    const selectedOptions = Array.isArray(newValue) ? newValue : [newValue];
    setSelectedUsers(selectedOptions);
    setAssignmentData({
      ...assignmentData,
      listOfUsers: selectedOptions.map((option) => ({ userId: option.value })),
    });
  };

  return (
    <div className="card">
      <ul
        className="nav nav-tabs cardHeaderHegiht"
        id="myTab"
        role="tablist"
        style={{ borderBottom: "1px solid rgb(222 226 230)" }}
      >
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link card-title tab-btn cardHeaderHegiht ${
              activeTab === "tab1" ? "active" : ""
            }`}
            id="tab1-tab"
            onClick={() => handleTabClick("tab1")}
            type="button"
            role="tab"
            aria-controls="tab1-tab-pane"
            aria-selected={activeTab === "tab1"}
          >
            Media Source Details
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link card-title cardHeaderHegiht tab-btn ${
              activeTab === "tab3" ? "active" : ""
            }`}
            id="tab3-tab"
            onClick={() => handleTabClick("tab3")}
            type="button"
            role="tab"
            aria-controls="tab3-tab-pane"
            aria-selected={activeTab === "tab3"}
          >
            Assigner Information
          </button>
        </li>
      </ul>
      <div className="tab-content card-body" id="myTabContent">
        <div
          className={`tab-pane fade ${
            activeTab === "tab1" ? "show active" : ""
          }`}
          id="tab1-tab-pane"
          role="tabpanel"
          aria-labelledby="tab1-tab"
          tabIndex={0}
        >
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="alert alert-danger text-danger" role="alert">
                {error}
              </div>
            )}
            <div className="row">
              <div className="col-12 col-lg-6 mb-3">
                <label htmlFor="sourceId" className="form-label">
                  Source ID
                </label>
                <input
                  type="number"
                  id="sourceId"
                  name="sourceId"
                  className="form-control form-control-lg"
                  placeholder="Source ID"
                  value={formData.sourceId}
                  disabled
                  required
                />
              </div>
              <div className="col-12 col-lg-6 mb-3">
                <label htmlFor="sourceName" className="form-label">
                  Source Name{requiredField}
                </label>
                <input
                  type="text"
                  id="sourceName"
                  name="sourceName"
                  className="form-control form-control-lg"
                  placeholder="Source Name"
                  value={formData.sourceName}
                  onChange={(e) =>
                    setFormData({ ...formData, sourceName: e.target.value })
                  }
                  required
                />
              </div>
              <div className="col-12 col-lg-6 mb-3">
                <label className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleCheckboxChange}
                  />
                  <span className="form-check-label">Is Active</span>
                </label>
              </div>
            </div>
          </form>
        </div>
        <div
          className={`tab-pane fade ${
            activeTab === "tab3" ? "show active" : ""
          }`}
          id="tab3-tab-pane"
          role="tabpanel"
          aria-labelledby="tab3-tab"
          tabIndex={0}
        >
          <form onSubmit={handleAssignmentSubmit}>
            <div className="row">
              <div className="col-12 col-lg-6 mb-3">
                <label className="form-label">
                  Assign Users{requiredField}
                </label>
                <Select
                  options={userOptions}
                  value={selectedUsers}
                  onChange={handleMultiSelectChange}
                  isMulti
                  className={`basic-multi-select ${
                    errors.assignmentType ? "is-invalid" : ""
                  }`}
                  classNamePrefix="select"
                  placeholder="Select users to assign"
                  isLoading={isLoadingUsers}
                  noOptionsMessage={() => "No users available"}
                />
                {errors.assignmentType && (
                  <div className="text-danger mt-1 small">
                    {errors.assignmentType}
                  </div>
                )}
                <div className="form-text">
                  Select multiple users to assign to this media source
                </div>
              </div>
            </div>
          </form>
        </div>
        <div>
          {activeTab !== "tab3" ? (
            <>
              <button
                type="submit"
                className="btn btn-primary me-2"
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? "Updating..." : "Update Media Source"}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-primary me-2"
                onClick={handleAssignmentSubmit}
                disabled={isSubmittingAssignment}
              >
                {isSubmittingAssignment ? "Saving..." : "Save Assigner"}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpdateCard;
